// Listens for `fcm-navigate` postMessage events from the FCM service worker
// and routes the focused tab to the deep-link path the SW computed.
//
// Flow:
//   1. User taps a push notification while the PWA is closed/backgrounded.
//   2. `public/firebase-messaging-sw.js` intercepts the click, resolves the
//      deep path from `event.notification.data` (mirrors
//      `src/core/fcm/deep-link.ts`), focuses the existing tab if present,
//      and postMessages `{ type: 'fcm-navigate', path }`.
//   3. This hook receives the message and invokes `navigate(path)` through
//      react-router — no full page reload, the single-page state stays
//      intact.
//
// Registered once in AppShell (alongside useFcmAutoRegister + useFcmForeground).

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '@/core/logger'

export function useFcmNavigation(): void {
  const navigate = useNavigate()
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; path?: string } | null
      if (!data || data.type !== 'fcm-navigate' || typeof data.path !== 'string') return
      try {
        navigate(data.path)
      } catch (err) {
        logger.w('FCM navigate failed', err)
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])
}
